import type { FC } from 'react';

/**
 * The dashboard's combined revenue / users / rentals chart.
 *
 * Ports the prototype's `viewBox="0 0 580 180"`, its four horizontal grid lines at y=30/75/120/165
 * and its 9px axis labels. The prototype hard-codes a single hand-drawn bezier; this builds the
 * same shape from real data, so the curve moves but the frame it is drawn in does not.
 *
 * Hand-rolled rather than charted with a library: webPanel/CLAUDE.md §0 makes the prototype the
 * pixel-perfect source of truth, and porting its geometry is the only way to honour that.
 */
export interface AreaLineSeries {
  id: string;
  label: string;
  /** A CSS colour — pass an @theme token, never a literal from the prototype. */
  color: string;
  values: number[];
  /** Filled area under the line. Reserved for the primary series. */
  area?: boolean;
  dashed?: boolean;
}

interface AreaLineChartProps {
  series: AreaLineSeries[];
  xLabels: string[];
  /**
   * Formats the y-axis value at a given grid line. The chart works out what value each line
   * actually represents and asks for its label, so the number always matches the height it
   * is drawn at. Omit to hide the axis.
   */
  formatY?: (value: number) => string;
  height?: number;
}

const WIDTH = 580;
const HEIGHT = 180;
const TOP = 12;
const BOTTOM = 165;
const GRID_LINES = [30, 75, 120, 165];

/** Maps one series onto the chart box. A flat series sits on the baseline rather than mid-air. */
const pointsFor = (values: number[], max: number): { x: number; y: number }[] => {
  if (values.length === 0) return [];
  const step = values.length > 1 ? WIDTH / (values.length - 1) : 0;
  return values.map((value, index) => ({
    x: Math.round(index * step * 100) / 100,
    y: max === 0 ? BOTTOM : BOTTOM - ((value / max) * (BOTTOM - TOP)),
  }));
};

const toPath = (points: { x: number; y: number }[]): string =>
  points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${Math.round(p.y * 100) / 100}`).join(' ');

const AreaLineChart: FC<AreaLineChartProps> = ({ series, xLabels, formatY, height = 180 }) => {
  // One shared scale across every series keeps the lines comparable; scaling each to its own
  // maximum would make a tiny series look identical to a huge one.
  const max = Math.max(1, ...series.flatMap((s) => s.values));

  return (
    <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} className="w-full" style={{ height }} role="img">
      <defs>
        {series
          .filter((s) => s.area)
          .map((s) => (
            <linearGradient key={s.id} id={`area-${s.id}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={s.color} stopOpacity="0.3" />
              <stop offset="100%" stopColor={s.color} stopOpacity="0" />
            </linearGradient>
          ))}
      </defs>

      {GRID_LINES.map((y) => (
        <line key={y} x1="0" y1={y} x2={WIDTH} y2={y} stroke="var(--color-divider)" strokeWidth="1" />
      ))}

      {series.map((s) => {
        const points = pointsFor(s.values, max);
        if (points.length === 0) return null;
        const line = toPath(points);
        return (
          <g key={s.id}>
            {s.area ? (
              <path d={`${line} L${WIDTH},${BOTTOM} L0,${BOTTOM} Z`} fill={`url(#area-${s.id})`} />
            ) : null}
            <path
              d={line}
              fill="none"
              stroke={s.color}
              strokeWidth={s.area ? 2.5 : 2}
              strokeLinecap="round"
              strokeDasharray={s.dashed ? '6 3' : undefined}
            />
          </g>
        );
      })}

      {xLabels.map((label, index) => (
        <text
          key={label + String(index)}
          x={(index / Math.max(1, xLabels.length - 1)) * WIDTH}
          y="178"
          textAnchor={index === 0 ? 'start' : index === xLabels.length - 1 ? 'end' : 'middle'}
          fontSize="9"
          fill="var(--color-ink-soft)"
        >
          {label}
        </text>
      ))}

      {formatY
        ? // The baseline (the last grid line) is always zero, so it is not labelled.
          GRID_LINES.slice(0, -1).map((y) => (
            <text key={y} x="-2" y={y + 2} fontSize="9" fill="var(--color-ink-soft)">
              {formatY(((BOTTOM - y) / (BOTTOM - TOP)) * max)}
            </text>
          ))
        : null}
    </svg>
  );
};

export default AreaLineChart;
